import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import { editOptionAndPrice } from '../../api/endpoints/postSale';
import type { PostSaleResponse } from '../../api/types/postSale';
import type { ReservationReportRow } from '../../api/types/reports';
import type { ReservationCapabilities } from './capabilities';
import { ApiErrorAlert } from '../ApiErrorAlert';

/**
 * Post-sale operation toolbar for the shared `ReservationDetailPage`. Only the
 * "Opsiyon / Fiyat" operation (`ShoppingCart/EditOptionAndPrice`) is exposed here — the
 * destructive refund/open/change/edit/note operations and the generic `/Void` were
 * removed from the UI (see reservation/capabilities.ts scope note). Cancel lives on the
 * row card in `ReservationDetailPage` itself.
 *
 * On success it fires `onDone` (the page refetches) and forwards the returned cart so the
 * audit panel can read `shoppingCart.processHistories`.
 */
export function PostSaleActions({
  row,
  caps,
  onDone,
}: {
  row: ReservationReportRow;
  caps: ReservationCapabilities;
  onDone: (res: PostSaleResponse) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [expireDate, setExpireDate] = useState('');
  const [expireTime, setExpireTime] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      editOptionAndPrice({
        shoppingCartId: row.shoppingCartId ?? '',
        pnr: row.pnr,
        expireDate: expireDate || undefined,
        expireTime: expireTime || undefined,
      }),
    onSuccess: (res) => {
      enqueueSnackbar('Opsiyon / Fiyat işlemi tamamlandı.', { variant: 'success' });
      setOpen(false);
      setExpireDate('');
      setExpireTime('');
      onDone(res);
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  if (!caps.editOptionAndPrice) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PriceChangeOutlinedIcon />}
        disabled={!row.shoppingCartId}
        onClick={() => setOpen(true)}
      >
        Opsiyon / Fiyat
      </Button>

      <Dialog open={open} onClose={() => !mutation.isPending && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Opsiyon / Fiyat{row.pnr ? ` — ${row.pnr}` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Opsiyon Tarihi"
              placeholder="GG.AA.YYYY"
              value={expireDate}
              onChange={(e) => setExpireDate(e.target.value)}
            />
            <TextField
              size="small"
              label="Opsiyon Saati (SS:DD)"
              value={expireTime}
              onChange={(e) => setExpireTime(e.target.value)}
            />
            <ApiErrorAlert error={mutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={mutation.isPending}>
            Vazgeç
          </Button>
          <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'İşleniyor…' : 'Uygula'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
